import React from 'react';

import { mentionType } from '@aline/common';
import { Box, Typography } from '@mui/material';
import type { PlateElementProps } from 'platejs/react';
import { PlateElement, useReadOnly, useSelected } from 'platejs/react';
import type { ReportColumnMentionElement as TReportColumnMentionElement } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorMention/utils';
import {
  ChipRemoveIcon,
  getChipRemoveButtonStyles,
  getEditorChipStyles,
} from 'src/components/ai/chat/ChatPanel/ChatEditor/utils';
import { makeStyles } from 'tss-react/mui';

import { ColumnIcon } from './ColumnIcon';

const useStyles = makeStyles()(theme => ({
  chip: {
    ...getEditorChipStyles(theme),
  },
  chipSelected: {
    borderColor: theme.palette.primary.main,
  },
  ...getChipRemoveButtonStyles(),
}));

export function ReportColumnMentionElement(
  props: PlateElementProps<TReportColumnMentionElement>
): JSX.Element {
  const { editor, element } = props;
  const readOnly = useReadOnly();
  const selected = useSelected();
  const { classes } = useStyles();

  if (element.type !== mentionType || element.mentionTypeName !== 'report-column') {
    return (
      <PlateElement {...props} as="span">
        <Box component="span">{props.children}</Box>
      </PlateElement>
    );
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    editor.tf.removeNodes({ at: editor.api.findPath(element) });
  };

  return (
    <PlateElement
      {...props}
      as="span"
      attributes={{
        ...props.attributes,
        contentEditable: false,
        'data-column-id': element.columnId,
        'data-slate-mention': true,
      }}
    >
      <Box className={`${classes.chip} ${selected ? classes.chipSelected : ''}`} component="span">
        {props.children}
        <ColumnIcon columnId={element.columnId} />
        <Typography noWrap component="span">
          {element.columnName || 'Column'}
        </Typography>
        {!readOnly && (
          <span className={classes.chipRemoveButton} onClick={handleRemove}>
            <ChipRemoveIcon />
          </span>
        )}
      </Box>
    </PlateElement>
  );
}
